import { Stack, Theme, useTheme } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { isNullish, uniqueBy } from 'remeda';
import {
  DynamicIcon,
  FormAutocompleteProps,
  FormLabel,
  PlatformUserAvatar,
} from 'src/components';
import { SelectMenu, SelectMenuOption } from 'src/components/SelectMenu';
import { SEARCH_DEBOUNCE_DELAY } from 'src/constants/delays';
import { componentsFontSizes } from 'src/constants/layout';
import { usePlatformUsers } from 'src/graphql';
import { extractDisabledUserTextFromName } from 'src/graphql/entities/platform-user';
import { mapPlatformUsersToSortedOptions } from 'src/graphql/entities/platform-user/platform-user.mapping';
import { DEFAULT_SEARCH_PLATFORM_USERS_TARGET_IDS } from 'src/graphql/entities/search/search-platform-user';
import { ConditionInput } from 'src/graphql/types';
import { useDebouncedValue } from 'src/utils/hooks/use-debounced-value';
import { toSxArray } from 'src/utils/sx';

export type AccountManagerFormValue = {
  id: string;
  label: string;
  partnerId?: number;
  avatarFileKey?: string | null;
};

const ACTIVE_USER_FILTERS: ConditionInput[] = [
  {
    attribute: 'isActive',
    operator: '=',
    value: 'true',
  },
];

const AccountManagerAvatar = ({
  option,
}: {
  option: Pick<AccountManagerFormValue, 'label' | 'partnerId' | 'avatarFileKey'>;
}) => {
  const theme = useTheme();

  return (
    <PlatformUserAvatar
      partnerId={option.partnerId}
      avatarFileKey={option.avatarFileKey}
      name={extractDisabledUserTextFromName(option.label)}
      sx={{
        width: '1.25rem',
        height: '1.25rem',
        fontSize: theme.utils.important(componentsFontSizes.coloredAvatar),
      }}
    />
  );
};

export const AccountManagerSelectMenuField = ({
  field,
  partnerId,
  disabled,
}: {
  field: FormAutocompleteProps;
  partnerId?: number;
  disabled?: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_DELAY);
  const requestedSearchRef = useRef<string | null>(null);
  const [fetchedSearch, setFetchedSearch] = useState<string | null>(null);

  const searchText = search === '' ? '' : debouncedSearch;

  const { platformUsers, loading, isLoadingMore, loadMore, hasMore } = usePlatformUsers(
    {
      partnerId,
      shouldIncludePlatformPartnerId: false,
      limit: 50,
      filters: ACTIVE_USER_FILTERS,
      searchParams: searchText
        ? {
            searchTerm: searchText,
            targetIdAttributes: DEFAULT_SEARCH_PLATFORM_USERS_TARGET_IDS,
          }
        : undefined,
    },
    {
      skip: !open || isNullish(partnerId),
    },
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    requestedSearchRef.current = searchText;
  }, [open, searchText]);

  useEffect(() => {
    if (!loading && requestedSearchRef.current !== null) {
      setFetchedSearch(requestedSearchRef.current);
    }
  }, [loading]);

  const userOptions = useMemo(() => {
    if (fetchedSearch !== searchText) {
      return [];
    }

    const mapped = mapPlatformUsersToSortedOptions(platformUsers) ?? [];
    return uniqueBy(mapped, (option) => option.id);
  }, [fetchedSearch, platformUsers, searchText]);

  const selectMenuOptions = useMemo<SelectMenuOption[]>(
    () =>
      userOptions.map((option) => ({
        value: option.id,
        title: option.label,
        icon: <AccountManagerAvatar option={option} />,
      })),
    [userOptions],
  );

  return (
    <Stack direction="row" alignItems="center" px={2} key={field.label}>
      <FormLabel label={field.label} />
      <Stack direction="row" width="100%" gap={1}>
        <Controller
          name={field.name}
          rules={{ required: field.required }}
          render={({ field: controllerField }) => {
            const value = (controllerField.value ??
              field.defaultValue ??
              null) as AccountManagerFormValue | null;

            return (
              <SelectMenu
                label={field.label ?? 'Account Manager'}
                options={selectMenuOptions}
                value={value?.id ?? null}
                onChange={(selectedId) => {
                  const selectedOption = userOptions.find(
                    (option) => option.id === selectedId,
                  );

                  if (selectedOption) {
                    controllerField.onChange(selectedOption);
                  }
                }}
                triggerLabel={value?.label}
                triggerIcon={
                  value?.label ? (
                    <AccountManagerAvatar option={value} />
                  ) : (
                    <DynamicIcon name="user" library="lucide" />
                  )
                }
                placeholder="Assign"
                search
                searchPlaceholder="Search account managers"
                searchValue={search}
                onSearchChange={setSearch}
                loading={Boolean(loading)}
                hasMore={Boolean(hasMore && selectMenuOptions.length > 0)}
                onLoadMore={loadMore}
                loadingMore={Boolean(isLoadingMore)}
                onOpenChange={(next) => {
                  setOpen(next);
                  if (!next) {
                    setSearch('');
                  }
                }}
                disabled={Boolean(
                  disabled || field.disabled || field.autocompleteProps?.disabled,
                )}
                emptyText="No account managers found."
                variant="inline"
                plainOptionIcons
                sx={[
                  { width: '100%' },
                  ...toSxArray(field.inputProps?.sx),
                  (theme: Theme) => ({
                    [theme.breakpoints.down('sm')]: {
                      fontSize: theme.utils.important('1rem'),
                    },
                  }),
                ]}
              />
            );
          }}
        />
      </Stack>
    </Stack>
  );
};
